/**
 * THE PER-MOVE ASSISTANT's state (fix F-D / D2) — V2-only, web-only, and
 * deliberately separate from `rail/analysis/analysisStore.ts`: that store is
 * the self-analysis ENGINE readout, this one is the hints the server sends
 * back for an assisted ("Play and Learn") game, keyed by the ply they belong
 * to so an older hint can still be reopened after later moves have landed.
 *
 * `hints` is keyed by the API's own `move_index` (1-based — "after N plies"),
 * the same convention `useV2Ui.viewPly` / `MoveList`'s `goTo` use, so a hint's
 * key doubles as the ply to browse to when it is opened.
 *
 * Reset together with `analysisStore` on a new game spawn (`rail/Rail/model.ts`):
 * an assisted game's hints belong to ONE table, and the old screen never
 * carried them across a rematch either (PlayChess has no equivalent state at
 * all — the notation itself held `hint`/`hint2` per move, L732-745).
 *
 * CLASSIFICATION IS THE SERVER'S (fix L / decision 1, owner 2026-10-06):
 * `label` is never computed here or anywhere else in this tree — `game/
 * start_analyzing_move` (`chess/app/views.py` `analyze_move`) already
 * classifies every ply with the backend's own adaptive thresholds (defaults
 * blunder > 250 cp / mistake > 150 / inaccuracy > 75 of the mover's own
 * `score_change`, scaled 20-50% above a prior score of |500|+ —
 * `chess/app/utils.py`), and the frontend engine runs at a different
 * depth/time with no access to them. Two classifiers would be two answers
 * for one move, so `hintText.ts`'s `buildHint` only ever COPIES the
 * server's own `blunder`/`mistake`/`inaccuracy` flags onto `label`.
 *
 * PENDING (fix L / L2): `pending` is the fen a POST was sent for, so a
 * response can be matched back to the exact position it asked about (and
 * dropped for any other — a stale table after a rematch, or an unknown
 * ply).
 *
 * NO AUTO-OPEN (owner, 2026-10-07): "don't auto open hint card — only when
 * user clicks it, then only open." `open` is the ONLY way `openIndex` is
 * ever set — nothing in this store (or `hooks/useAssistEffects.ts`) decides
 * FOR the player which ply to show. This replaced an earlier `openLatest` /
 * `dismissed` pair (the auto-open-on-a-flagged-move mechanism and the
 * "closed once, never reopens itself" bookkeeping it needed) — both are
 * gone, not merely unused, since nothing auto-opens to need guarding
 * against any more.
 */
import {create} from 'zustand';

export type HintLabel = '' | 'Inaccuracy' | 'Mistake' | 'Blunder';

export type Hint = {
  label: HintLabel;
  text: string;
  text2: string;
  /**
   * fix L / L2: whose move this is. Used to gate the chip/card through two
   * review rounds (first mine-and-flagged-only, then mine alone); fix Z1.1
   * review, FURTHER (owner, 2026-10-07: "also show hint for both players")
   * dropped that gate too — the chip and the card are now EITHER side's.
   * The field stays (`buildHint` still carries it straight from
   * `pending.mine`) since it is still true information about the hint, but
   * nothing in this tree reads it any more outside tests.
   */
  mine: boolean;
  /** the response's own `move` (the request sent the core's SAN) */
  san: string;
  /** "12." / "12…" (`getChessMoveNumber`), stored once for the card's header */
  moveNo: string;
  /** the server's best SAN line from the position BEFORE this move, or '' */
  bestLine: string;
};

export type AssistStore = {
  /** keyed by move_index (1-based) */
  hints: Record<number, Hint>;
  /** the hint card's own move_index, or none */
  openIndex: number | null;
  /** fix L / L2: the fen + mover a POST was sent for, until its response lands or is dropped */
  pending: Record<number, {fen: string; mine: boolean}>;
  markPending: (moveIndex: number, entry: {fen: string; mine: boolean}) => void;
  setHint: (moveIndex: number, hint: Hint) => void;
  /** owner, 2026-10-07: the ONLY way a card opens — the player's own press */
  open: (moveIndex: number) => void;
  close: () => void;
  reset: () => void;
};

export const useAssistStore = create<AssistStore>(set => ({
  hints: {},
  openIndex: null,
  pending: {},
  markPending: (moveIndex, entry) =>
    set(s => ({...s, pending: {...s.pending, [moveIndex]: entry}})),
  setHint: (moveIndex, hint) =>
    set(s => {
      const pending = {...s.pending};
      delete pending[moveIndex];
      return {...s, hints: {...s.hints, [moveIndex]: hint}, pending};
    }),
  open: moveIndex =>
    set(s => (s.openIndex === moveIndex ? s : {...s, openIndex: moveIndex})),
  close: () => set(s => (s.openIndex === null ? s : {...s, openIndex: null})),
  reset: () =>
    set(s =>
      Object.keys(s.hints).length === 0 &&
      s.openIndex === null &&
      Object.keys(s.pending).length === 0
        ? s
        : {...s, hints: {}, openIndex: null, pending: {}},
    ),
}));

/**
 * Forget every hint, from outside React — the same "a new game spawned"
 * guard that calls `resetAnalysis` (`rail/Rail/model.ts`).
 */
export const resetAssist = (): void => {
  useAssistStore.getState().reset();
};

export default useAssistStore;
